import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * The top header's and bottom nav's icons (top-header handoff §3, §7): 2px round strokes on a
 * 24 grid. The bottom-nav glyphs (ticket, people, clubs, calendar) are Lucide-style stand-ins
 * the handoff drew for the prototype — flagged there as substitutes, kept until a drawn set lands.
 */
const round = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };

export const SearchGlyph = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="square" aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" />
  </svg>
);

export const CloseGlyph = ({ size = 16, width = 2.5 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="square" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const PlusGlyph = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" aria-hidden="true">
    <path d="M12 4v16M4 12h16" />
  </svg>
);

export const MessageGlyph = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z" />
  </svg>
);

export const BellGlyph = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <path d="M6 16V10a6 6 0 0 1 12 0v6l2 2H4z" /><path d="M10 21h4" />
  </svg>
);

export const ChevronGlyph = ({ size = 12, color = '#9CA3AF', width = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 9l-7 7-7-7" />
  </svg>
);

export const ClockGlyph = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);

export const HomeGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <path d="M4 11l8-7 8 7v9h-5v-6H9v6H4z" />
  </svg>
);

export const TicketGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <path d="M3 8a2 2 0 0 0 0 4v0a2 2 0 0 0 0 4v2h18v-2a2 2 0 0 1 0-4a2 2 0 0 1 0-4V6H3z" />
    <path d="M14 6v12" strokeDasharray="2 3" />
  </svg>
);

export const PeopleGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" /><path d="M18 14.3a6.5 6.5 0 0 1 3.5 5.7" />
  </svg>
);

export const ClubsGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <circle cx="12" cy="7" r="3" /><circle cx="6" cy="16" r="3" /><circle cx="18" cy="16" r="3" />
    <path d="M10.3 9.5 7.7 13.5M13.7 9.5l2.6 4M9 16h6" />
  </svg>
);

export const CalendarGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <rect x="3.5" y="5" width="17" height="15" rx="3" /><path d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);

/** The MESSAGES tab (bottom-nav v2): a square-cornered bubble with a terminal prompt inside. */
export const MessagesTabGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...round}>
    <path d="M3 4h18v13H11l-5 4v-4H3z" />
    <path d="M7 8.5l2.5 2L7 12.5" />
    <path d="M12 12.5h4" />
  </svg>
);

/**
 * The FRIENDS tab (bottom-nav v2, variant "jump"): two friends either side of a portal.
 * Tapping the tab sends the right one THROUGH the portal — a parabola (linear X, ease-out
 * up / ease-in down Y), the portal closing behind them — to land beside the left one,
 * then the pair centres. Leaving the tab puts everyone back, no reverse jump. Landing on
 * the route directly (a reload, a deep link) draws the final state with no animation.
 *
 *   active   the tab is current
 *   animate  false draws the state for `active` without any motion (the initial mount)
 */
const JUMP_TAKEOFF_MS = 210;
const JUMP_CENTRE_AT_MS = 600;
export const FRIENDS_JUMP_MS = { takeoff: JUMP_TAKEOFF_MS, centre: JUMP_CENTRE_AT_MS };

export function FriendsGlyph({ size = 18, active = false, animate = true }) {
  const uid = useId();
  const reduced = prefersReducedMotionSafe();
  // stage: 0 rest · 1 in flight · 2 landed and centred. `airborne` is the up-half of the arc.
  const [stage, setStage] = useState(active ? 2 : 0);
  const [airborne, setAirborne] = useState(false);
  const [moving, setMoving] = useState(false);   // false = no transitions (initial mount / reduced)
  const timers = useRef([]);
  const wasActive = useRef(active);

  useEffect(() => {
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    clear();
    const became = active && !wasActive.current;
    const left = !active && wasActive.current;
    wasActive.current = active;
    if (became) {
      if (!animate || reduced) { setMoving(false); setStage(2); setAirborne(false); return undefined; }
      setMoving(true);
      setStage(1);
      setAirborne(true);
      timers.current.push(setTimeout(() => setAirborne(false), JUMP_TAKEOFF_MS));
      timers.current.push(setTimeout(() => setStage(2), JUMP_CENTRE_AT_MS));
    } else if (left) {
      setMoving(!reduced);
      setStage(0);
      setAirborne(false);
    }
    return clear;
  }, [active, animate, reduced]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const ease = (ms, curve) => (moving ? `${ms}ms ${curve}` : '0ms linear');
  // Friend A (left) — only moves at the centring stage, or home when leaving.
  const aX = stage === 2 ? 4.25 : 0;
  const aStyle = { transform: `translateX(${aX}px)`, transition: `transform ${stage === 0 ? ease(220, 'cubic-bezier(0,0,0.2,1)') : ease(300, 'cubic-bezier(0,0,0.2,1)')}` };
  // Friend B (right) — the outer g carries X (linear through the air), the inner g carries Y.
  const bX = stage === 0 ? 0 : stage === 1 ? -8.5 : -4.25;
  const bXStyle = { transform: `translateX(${bX}px)`, transition: `transform ${stage === 0 ? ease(320, 'cubic-bezier(.4,0,.2,1)') : stage === 1 ? ease(420, 'linear') : ease(300, 'cubic-bezier(0,0,0.2,1)')}` };
  const bYStyle = { transform: `translateY(${airborne ? -7 : 0}px)`, transition: `transform ${airborne ? ease(210, 'cubic-bezier(0.2,0.6,0.4,1)') : ease(210, 'cubic-bezier(0.6,0,0.8,0.4)')}` };
  // The portal closes behind the jumper (from 360ms) and reopens when everyone goes home.
  const closed = stage !== 0;
  const portalStyle = {
    transformBox: 'fill-box', transformOrigin: 'center',
    transform: `scaleY(${closed ? 0 : 1})`, opacity: closed ? 0 : 1,
    transition: closed
      ? `transform ${ease(180, 'cubic-bezier(0.4,0,1,1)')} ${moving ? 360 : 0}ms, opacity ${ease(180, 'cubic-bezier(0.4,0,1,1)')} ${moving ? 360 : 0}ms`
      : `transform ${ease(200, 'cubic-bezier(0,0,0.2,1)')} ${moving ? 160 : 0}ms, opacity ${ease(160, 'linear')} ${moving ? 160 : 0}ms`,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" data-friends-stage={stage} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FD199F" /><stop offset=".35" stopColor="#B5B1FF" />
          <stop offset=".65" stopColor="#22ECFB" /><stop offset="1" stopColor="#FAE1B4" />
        </linearGradient>
      </defs>
      <ellipse cx="12" cy="12" rx="2.4" ry="9" fill="none" stroke={`url(#${uid})`} strokeWidth="2" style={portalStyle} />
      <g fill="currentColor" style={aStyle} data-friend="a">
        <circle cx="4.5" cy="7.6" r="2.3" /><path d="M1.3 21v-6.2a3.2 3.2 0 0 1 6.4 0V21z" />
      </g>
      <g style={bXStyle} data-friend="b">
        <g fill="currentColor" style={bYStyle}>
          <circle cx="19.5" cy="7.6" r="2.3" /><path d="M16.3 21v-6.2a3.2 3.2 0 0 1 6.4 0V21z" />
        </g>
      </g>
    </svg>
  );
}

function prefersReducedMotionSafe() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}
